import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { AddEditDeleteBar1 } from '../stable/AddEditDeleteBar1';
import {
  createHumanRelationship,
  updateHumanRelationship,
  deleteHumanRelationship,
  createHumanWithRelationship,
  deleteHuman,
} from '../../store/map3PendingSlice';
import { setHighlightedHumanUuid } from '../../store/map3UISlice';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertActions,
} from '../../components_catalyst/alert';
import { Button } from '../../components_catalyst/button';
import type { Human } from '@mm-shared/types/Human';
import type { HumanRelationship } from '@mm-shared/types/HumanRelationship';
import { toRelationshipType, hasOverlappingDateRange } from '@mm-shared/types/HumanRelationship';
import { v4 as uuidv4 } from 'uuid';

type TeamMemberRow = {
  relationship: HumanRelationship;
  human: Human | undefined;
};

type FormData = {
  humanUuid: string;
  humanName: string;
  isNewHuman: boolean;
  relationshipName: string;
  startDate: string;
  endDate: string;
};

const emptyForm: FormData = {
  humanUuid: '',
  humanName: '',
  isNewHuman: false,
  relationshipName: '',
  startDate: '',
  endDate: '',
};

export function Map3Team() {
  const [searchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const dispatch = useDispatch<AppDispatch>();

  const [isAdding, setIsAdding] = useState(false);
  const [editingRelationshipUuid, setEditingRelationshipUuid] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [hoveredRowUuid, setHoveredRowUuid] = useState<string | null>(null);
  const [humanSearchQuery, setHumanSearchQuery] = useState('');
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleInputRef = useRef<HTMLInputElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // Delete person confirmation state
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [humanToDelete, setHumanToDelete] = useState<Human | null>(null);

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );

  const humans = useSelector((state: RootState) => state.map3Data.humans);
  const humanRelationships = useSelector((state: RootState) => state.map3Data.humanRelationships);
  const highlightedHumanUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedHumanUuid,
  );

  // Clear highlight when Map3Team unmounts (e.g., navigating away from Team tab)
  useEffect(() => {
    return () => {
      dispatch(setHighlightedHumanUuid(null));
    };
  }, [dispatch]);

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // Filter relationships for this objective
  const objectiveRelationships = useMemo(() => {
    if (!objective) return [];
    return humanRelationships.filter(
      (r) => r.targetUuid === objective.uuid && r.targetType === 'objective',
    );
  }, [humanRelationships, objective]);

  // Build team member rows: relationship + human lookup
  const teamMembers: TeamMemberRow[] = useMemo(() => {
    return objectiveRelationships.map((rel) => ({
      relationship: rel,
      human: humans.find((h) => h.uuid === rel.humanUuid),
    }));
  }, [objectiveRelationships, humans]);

  // Filter humans for the search/autocomplete
  const filteredHumans = useMemo(() => {
    if (!humanSearchQuery.trim()) return humans;
    const query = humanSearchQuery.toLowerCase();
    return humans.filter((h) => h.name.toLowerCase().includes(query));
  }, [humans, humanSearchQuery]);

  // Compute unique relationshipName values for auto-suggest
  const uniqueRelationshipNames = useMemo(() => {
    const names = new Set<string>();
    for (const rel of humanRelationships) {
      if (rel.relationshipName?.trim()) {
        names.add(rel.relationshipName.trim());
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [humanRelationships]);

  // Filter suggestions based on current input (case-insensitive substring match)
  const filteredRoleSuggestions = useMemo(() => {
    const query = formData.relationshipName.trim().toLowerCase();
    if (!query) return [];
    return uniqueRelationshipNames.filter((name) =>
      name.toLowerCase().includes(query),
    );
  }, [uniqueRelationshipNames, formData.relationshipName]);

  // Close role dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node) &&
        roleInputRef.current &&
        !roleInputRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!objective) {
    return <div className="p-4 text-gray-500">Objective not found</div>;
  }

  const resetForm = () => {
    setFormData(emptyForm);
    setFormError(null);
    setIsAdding(false);
    setEditingRelationshipUuid(null);
    setHumanSearchQuery('');
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingRelationshipUuid(null);
    setFormData(emptyForm);
    setFormError(null);
    setHumanSearchQuery('');
  };

  const handleEdit = (row: TeamMemberRow) => {
    setEditingRelationshipUuid(row.relationship.uuid);
    setIsAdding(false);
    setFormData({
      humanUuid: row.relationship.humanUuid,
      humanName: row.human?.name || '',
      isNewHuman: false,
      relationshipName: row.relationship.relationshipName || '',
      startDate: row.relationship.startDate || '',
      endDate: row.relationship.endDate || '',
    });
    setFormError(null);
    setHumanSearchQuery(row.human?.name || '');
  };

  const handleRemove = async (relationshipUuid: string) => {
    const relationship = objectiveRelationships.find((r) => r.uuid === relationshipUuid);
    if (!relationship) return;
    await dispatch(deleteHumanRelationship(relationship));
  };

  /**
   * Opens the "Delete Person" confirmation Alert.
   * Delete removes the Human record entirely (not just the relationship).
   */
  const handleDeletePersonClick = (human: Human) => {
    setHumanToDelete(human);
    setDeleteAlertOpen(true);
  };

  /**
   * Confirmed delete: dispatches deleteHuman thunk which cascades removal
   * of the Human document and all related HumanRelationships.
   */
  const handleDeletePersonConfirm = async () => {
    if (!humanToDelete) return;
    setDeleteAlertOpen(false);
    await dispatch(deleteHuman(humanToDelete));
    setHumanToDelete(null);
    resetForm();
  };

  const handleDeletePersonCancel = () => {
    setDeleteAlertOpen(false);
    setHumanToDelete(null);
  };

  const handleSelectHuman = (human: Human) => {
    setFormData((prev) => ({
      ...prev,
      humanUuid: human.uuid,
      humanName: human.name,
      isNewHuman: false,
    }));
    setHumanSearchQuery(human.name);
  };

  const handleCreateNewHuman = () => {
    const name = humanSearchQuery.trim();
    if (!name) return;
    setFormData((prev) => ({
      ...prev,
      humanUuid: '',
      humanName: name,
      isNewHuman: true,
    }));
  };

  const handleSave = async () => {
    setFormError(null);

    // Validate required fields
    const name = formData.humanName;
    if (!name.trim()) {
      setFormError('Please select or create a team member.');
      return;
    }
    if (!formData.humanUuid && !formData.isNewHuman) {
      setFormError('Please select or create a team member.');
      return;
    }

    const relationshipType = formData.relationshipName.trim()
      ? toRelationshipType(formData.relationshipName)
      : 'team-member';

    if (editingRelationshipUuid) {
      // Update existing relationship
      const updates: Partial<HumanRelationship> = {
        relationshipName: formData.relationshipName || undefined,
        relationshipType,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
      };

      const result = await dispatch(
        updateHumanRelationship({
          uuid: editingRelationshipUuid,
          updates,
        }),
      );

      if (updateHumanRelationship.rejected.match(result)) {
        const payload = result.payload as string | undefined;
        if (payload && payload.includes('overlap')) {
          setFormError(
            'This person is already allocated to this objective for an overlapping period.',
          );
          return;
        }
        setFormError('Failed to update team member.');
        return;
      }
    } else {
      // Create new relationship (and possibly new human)
      const relUuid = uuidv4();
      // Build relationship without undefined values — Firestore rejects explicit
      // `undefined` fields (throws "Unsupported field value: undefined").
      const newRelationship: HumanRelationship = {
        uuid: relUuid,
        shortId: relUuid.slice(0, 10),
        humanUuid: formData.humanUuid || uuidv4(), // will be set below for new humans
        targetType: 'objective',
        targetUuid: objective.uuid,
        relationshipType,
        version: 1,
        createdDate: new Date().toISOString(),
        // Only include optional fields when they have a truthy value.
        // Omitting keys entirely is valid for Firestore; setting them to
        // `undefined` is not.
        ...(formData.relationshipName ? { relationshipName: formData.relationshipName } : {}),
        ...(formData.startDate ? { startDate: formData.startDate } : {}),
        ...(formData.endDate ? { endDate: formData.endDate } : {}),
      };

      if (formData.isNewHuman) {
        // Create new human + relationship atomically
        const humanUuid = uuidv4();
        const newHuman: Human = {
          uuid: humanUuid,
          shortId: humanUuid.slice(0, 10),
          name: formData.humanName.trim(),
          version: 1,
          createdDate: new Date().toISOString(),
        };
        newRelationship.humanUuid = humanUuid;

        const result = await dispatch(
          createHumanWithRelationship({ human: newHuman, relationship: newRelationship }),
        );

        if (createHumanWithRelationship.rejected.match(result)) {
          const payload = result.payload as string | undefined;
          if (payload && payload.includes('overlap')) {
            setFormError(
              'This person is already allocated to this objective for an overlapping period.',
            );
            return;
          }
          setFormError('Failed to add team member.');
          return;
        }
      } else {
        // Create relationship for existing human
        const result = await dispatch(createHumanRelationship(newRelationship));

        if (createHumanRelationship.rejected.match(result)) {
          const payload = result.payload as string | undefined;
          if (payload && payload.includes('overlap')) {
            setFormError(
              'This person is already allocated to this objective for an overlapping period.',
            );
            return;
          }
          setFormError('Failed to add team member.');
          return;
        }
      }
    }

    resetForm();
  };

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate && !endDate) return '';
    const start = startDate ? new Date(startDate).toLocaleDateString() : 'Start';
    const end = endDate ? new Date(endDate).toLocaleDateString() : 'Present';
    return `${start} - ${end}`;
  };

  return (
    <div className="space-y-4 p-4" role="region" aria-label="Team members">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <h2 id="team-heading" className="text-lg font-semibold">
          Team
        </h2>
        {canEdit && !isAdding && !editingRelationshipUuid && (
          <button
            onClick={handleAdd}
            className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
            aria-label="Add team member"
          >
            + Add member
          </button>
        )}
      </div>

      {/* Add / Edit Form */}
      {(isAdding || editingRelationshipUuid) && canEdit && (
        <div
          className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50"
          role="form"
          aria-label={editingRelationshipUuid ? 'Edit team member' : 'Add team member'}
        >
          {/* Human search/select */}
          <div>
            <label
              htmlFor="human-search"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Person
            </label>
            <div className="relative">
              <input
                id="human-search"
                type="text"
                value={humanSearchQuery}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  setHumanSearchQuery(inputValue);
                  // Clear any stale selection or new-person state whenever the
                  // user types something that no longer matches the committed name.
                  // This covers both the "existing human selected" case
                  // (humanUuid set) and the "new person created" case (isNewHuman).
                  const hasStaleSelection = formData.humanUuid !== '' || formData.isNewHuman;
                  if (hasStaleSelection && formData.humanName !== inputValue) {
                    setFormData((prev) => ({
                      ...prev,
                      humanUuid: '',
                      humanName: '',
                      isNewHuman: false,
                    }));
                  }
                }}
                placeholder="Search for a person..."
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoComplete="off"
                aria-label="Search for a person"
                disabled={!!editingRelationshipUuid}
              />
              {/* Dropdown for search results */}
              {humanSearchQuery.trim() &&
                !formData.humanUuid &&
                !formData.isNewHuman &&
                !editingRelationshipUuid && (
                  <div
                    className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    role="listbox"
                    aria-label="Search results"
                  >
                    {filteredHumans.map((human) => (
                      <button
                        key={human.uuid}
                        onClick={() => handleSelectHuman(human)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                        role="option"
                        aria-selected={false}
                      >
                        {human.name}
                        {human.email && (
                          <span className="text-gray-400 ml-2">{human.email}</span>
                        )}
                      </button>
                    ))}
                    {/* Create new option */}
                    <button
                      onClick={handleCreateNewHuman}
                      className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-t border-gray-100"
                      role="option"
                      aria-selected={false}
                    >
                      Create &quot;{humanSearchQuery.trim()}&quot;
                    </button>
                  </div>
                )}
              {/* Show selected state */}
              {(formData.humanUuid || formData.isNewHuman) && (
                <div className="mt-1 text-xs text-gray-500">
                  {formData.isNewHuman ? (
                    <span>New person: {formData.humanName}</span>
                  ) : (
                    <span>ID: {humans.find((h) => h.uuid === formData.humanUuid)?.shortId ?? formData.humanUuid}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Relationship name with auto-suggest */}
          <div>
            <label
              htmlFor="relationship-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Role
            </label>
            <div className="relative">
              <input
                id="relationship-name"
                ref={roleInputRef}
                type="text"
                value={formData.relationshipName}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, relationshipName: e.target.value }));
                  setIsRoleDropdownOpen(true);
                }}
                onFocus={() => setIsRoleDropdownOpen(true)}
                placeholder="e.g., Project Lead, Developer"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Role or relationship name"
                autoComplete="off"
              />
              {isRoleDropdownOpen && filteredRoleSuggestions.length > 0 && (
                <div
                  ref={roleDropdownRef}
                  className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  role="listbox"
                  aria-label="Role suggestions"
                >
                  {filteredRoleSuggestions.map((name) => (
                    <button
                      key={name}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, relationshipName: name }));
                        setIsRoleDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                      role="option"
                      aria-selected={formData.relationshipName === name}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date range */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label
                htmlFor="start-date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Start date
              </label>
              <input
                id="start-date"
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, startDate: e.target.value }))
                }
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Start date"
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="end-date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                End date
              </label>
              <input
                id="end-date"
                type="date"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, endDate: e.target.value }))
                }
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="End date"
              />
            </div>
          </div>

          {/* Error message */}
          {formError && (
            <div className="text-sm text-red-600" role="alert" aria-live="polite">
              {formError}
            </div>
          )}

          {/* Action buttons: Delete Person (left, red) | Cancel + Save (right) */}
          <div className="flex items-center justify-between pt-1">
            {/* Delete Person button — shown only when editing an existing member */}
            <div>
              {editingRelationshipUuid && canEdit && (() => {
                const editingRow = teamMembers.find(
                  (row) => row.relationship.uuid === editingRelationshipUuid,
                );
                const editingHuman = editingRow?.human;
                return editingHuman ? (
                  <button
                    onClick={() => handleDeletePersonClick(editingHuman)}
                    className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    aria-label={`Delete ${editingHuman.name} as a person record`}
                    data-testid="delete-person-button"
                  >
                    Delete Person
                  </button>
                ) : null;
              })()}
            </div>

            {/* Save and Cancel buttons */}
            <div className="flex gap-2">
              <button
                onClick={resetForm}
                className="px-3 py-1.5 text-sm text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {editingRelationshipUuid ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team member list */}
      {teamMembers.length === 0 && !isAdding && (
        <p className="text-gray-500 text-sm" data-testid="team-empty-state">
          No team members yet. Add someone to get started.
        </p>
      )}

      {teamMembers.length > 0 && (
        <ul className="divide-y divide-gray-100" aria-label="Team member list">
          {teamMembers.map((row) => (
            <li
              key={row.relationship.uuid}
              className="relative py-3 px-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onMouseEnter={() => setHoveredRowUuid(row.relationship.uuid)}
              onMouseLeave={() => setHoveredRowUuid(null)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-grow min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {row.human?.name || 'Unknown'}
                  </div>
                  {row.relationship.relationshipName && (
                    <div className="text-xs text-gray-500 truncate">
                      {row.relationship.relationshipName}
                    </div>
                  )}
                  {(row.relationship.startDate || row.relationship.endDate) && (
                    <div className="text-xs text-gray-400">
                      {formatDateRange(row.relationship.startDate, row.relationship.endDate)}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex-shrink-0 ml-2">
                    <AddEditDeleteBar1
                      isVisible={hoveredRowUuid === row.relationship.uuid}
                      showAdd={false}
                      showEdit={true}
                      showDelete={false}
                      showRemove={true}
                      showInfo={false}
                      showHighlight={true}
                      onEdit={() => handleEdit(row)}
                      onRemove={() => handleRemove(row.relationship.uuid)}
                      onHighlight={() =>
                        dispatch(setHighlightedHumanUuid(row.relationship.humanUuid))
                      }
                      isHighlighted={highlightedHumanUuid === row.relationship.humanUuid}
                    />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Delete Person confirmation Alert */}
      <Alert open={deleteAlertOpen} onClose={handleDeletePersonCancel}>
        <AlertTitle>Delete Person?</AlertTitle>
        <AlertDescription>
          <strong>Delete</strong> removes the {humanToDelete?.name || 'person'} record entirely —
          including all their relationships across all objectives. This cannot be undone.
          <br />
          <br />
          To only remove them from this team, use <strong>Remove</strong> (minus icon) instead.
        </AlertDescription>
        <AlertActions>
          <Button plain onClick={handleDeletePersonCancel}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeletePersonConfirm}
            data-testid="delete-person-confirm-button"
          >
            Delete Person
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
}
